import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';

import { InputField } from '@/components/Atoms/InputField/InputField';
import { InputFieldButton } from '@/components/Atoms/InputField/InputFieldButton';
import { LocalesContext } from '@/context/locales/LocalesProvider';
import { classMerge } from '@/utils/class-names';
import HappyIcon from '~/icons/Happy.svg';
import UnhappyIcon from '~/icons/Unhappy.svg';
import VisibilityIcon from '~/icons/Visibility.svg';
import VisibilityOffIcon from '~/icons/VisibilityOff.svg';

import { PasswordProps } from './types';

const criteriaContainerStyles = classMerge(
	'relative flex flex-col items-center w-full',
	'border border-neutral-grey bg-neutral-grey-4',
	'after:absolute after:w-3 after:h-3',
	'after:bg-neutral-grey-4 after:rounded-br-sm after:border-r after:border-b after:border-neutral-grey',
	'after:top-[calc(100%-5.3px)] after:rotate-45 after:left-[calc(50%-6px)]'
);

const criterionContainerStyles =
	'flex gap-x-2 items-center pb-3 last:pb-0 w-full text-body-3 font-medium';

const Password: FC<PasswordProps> = ({
	isNewPassword,
	authFailed,
	id,
	criteria = [],
	name,
	value = '',
	onChange: handleChange,
}) => {
	const { locales } = useContext(LocalesContext);

	const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);
	const [areCriteriaExpanded, setAreCriteriaExpanded] = useState(false);

	const criteriaAnimationWrapperStyles = classMerge(
		'w-full transition-max-height duration-300 ease-linear overflow-y-hidden',
		areCriteriaExpanded ? 'max-h-60 pb-2' : 'max-h-0'
	);

	const areCriteriaPresent = criteria.length > 0;
	const failedCriteria = criteria.find(({ isMet }) => !isMet);

	useEffect(() => {
		if (!areCriteriaPresent) {
			return;
		}

		if (value && failedCriteria && !areCriteriaExpanded) {
			setAreCriteriaExpanded(true);
		}

		if (value && !failedCriteria && areCriteriaExpanded) {
			setAreCriteriaExpanded(false);
		}
	}, [areCriteriaPresent, failedCriteria, areCriteriaExpanded, value]);

	const handleInputFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (!handleChange) {
			return;
		}

		handleChange(event);
	};

	const handleInputFieldFocus = () => {
		if (!areCriteriaPresent || areCriteriaExpanded) {
			return;
		}

		setAreCriteriaExpanded(true);
	};

	const handleInputFieldBlur = () => {
		if (!areCriteriaPresent || (value && failedCriteria)) {
			return;
		}

		setAreCriteriaExpanded(false);
	};

	const handlePasswordVisibilityToggle = () => {
		setIsPasswordRevealed(!isPasswordRevealed);
	};

	return (
		<div className="flex flex-col gap-y-3">
			{areCriteriaPresent && (
				<div className={criteriaAnimationWrapperStyles}>
					<div className={criteriaContainerStyles}>
						<ul className="flex flex-col p-[22px] w-full">
							{criteria.map(({ content, isMet, name: key }) => (
								<li className={criterionContainerStyles} key={key}>
									{isMet ? (
										<HappyIcon
											width="20px"
											height="20px"
											className="text-primary-entertainment"
										/>
									) : (
										<UnhappyIcon
											width="20px"
											height="20px"
											className="text-[#ff2828]"
										/>
									)}
									{content}
								</li>
							))}
						</ul>
					</div>
				</div>
			)}
			<InputField
				id={id}
				isValid={!Boolean(failedCriteria) && !authFailed}
				autoComplete={isNewPassword ? 'new-password' : 'current-password'}
				maxLength={128}
				name={name || locales.password}
				type={isPasswordRevealed ? 'text' : 'password'}
				value={value}
				onBlur={handleInputFieldBlur}
				onChange={handleInputFieldChange}
				onFocus={handleInputFieldFocus}
			>
				<InputFieldButton
					aria-label={locales['show-password']}
					icon={isPasswordRevealed ? VisibilityOffIcon : VisibilityIcon}
					onClick={handlePasswordVisibilityToggle}
				/>
			</InputField>
		</div>
	);
};

export default Password;
